import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Output, ViewEncapsulation, inject, input, signal } from '@angular/core';
import { AbstractControl, FormArray, FormControl, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import * as _ from 'lodash-es';
import { AlertService } from 'src/app/services/alert.service';
import { LoaderService } from 'src/app/services/loader.service';
import { AuthService } from 'src/app/services/auth.service';
import { ViewComponent } from '../../pages/view/view.component';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { FormGroupComponent } from '../../pages/form-group/form-group.component';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { UpperCasePipe } from '@angular/common';

export interface IValidable {
    isValid(key?: string): boolean;
}

@Component({
    selector: 'app-form-group-bookmarks',
    templateUrl: './form-group-bookmarks.component.html',
    styleUrls: ['./form-group-bookmarks.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [MatIcon, MatTooltip, UpperCasePipe, TranslatePipe]
})
export class FormGroupBookmarksComponent extends FormGroupComponent<any> {
    protected translate = inject(TranslateService);
    protected cd = inject(ChangeDetectorRef);


    readonly validable = input<IValidable>();
    readonly name = input<string>();
    readonly warnings = input<{
    predicate: () => {
        key: string;
        messages?: string[];
    }[];
}>();

    // @Input() headers: string[] = [];
    readonly headers = input<{
    key: string;
    errors?: string[];
    hasRedDot?: () => boolean;
    predicate?: () => boolean;
}[]>([]);

    // filter 
    get form_headers(): string[] {
        const headers = this.headers();
        if (headers.length) {
            return headers
                .filter(header => !header.predicate || header.predicate())
                .map(header => header.key)
                .filter(key => !!this.controls[key]);
        } else {
            return _.keys(this.controls).filter(key => !!this.controls[key]);
        }
    }

    // get form_headers() {
    //     if (this.headers.length) {
    //         return this.headers.filter(key => !!this.controls[key]);
    //     } else {
    //         return _.keys(this.controls).filter(key => !!this.controls[key]);//.filter(key => this.isFormGroup(this.controls[key]));
    //     }
    // }

    // A signal: the sub forms change their errors while the page is being checked (see FormGroupComponent.valid)
    readonly errors = signal<any>({});

    constructor() {
        super();

        this.subscription.add(this.formChange.subscribe((form) => {
            if (!this.form) return;
            this.errors.set(this.getFormErrors(form));
            this.subscription.add(this.form.valueChanges.subscribe(() => {
                this.errors.set(this.getFormErrors(form));
            }));
        }));
    }

    isFormGroup(control: AbstractControl) {
        return control instanceof FormGroup;
    }

    hasError(key?: string) {
        const errors = this.errors();
        const header = this.headers().find(h => h.key === key);
        if (key) {
            if (header?.errors) {
                return _.sumBy(header.errors, validatorKey => {
                    return _.sumBy(_.keys(errors || {}).filter(k => k.split('.').some(key_part => key_part === validatorKey)), k => errors[k].length);
                });
            } else {
                return _.sumBy(_.keys(errors || {}).filter(k => k.split('.').some(key_part => key_part === key)), k => errors[k].length);
            }
        } else {
            return _.keys(errors)?.length;
        }
    }

    getError(key: string): string[] | undefined {
        const errors = this.errors();
        const errorMessages: any = [];
        Object.keys(errors).forEach(errorKey => {
            if (errorKey.startsWith(key) && errors[errorKey].length > 0) {
                errorMessages.push(errorKey);
            }
        });
        return errorMessages.length ? errorMessages : undefined;

    }

    getErrorTooltip(key: string): string {
        const header = this.headers().find(h => h.key === key);

        const errors = header?.errors ? header.errors.flatMap(errorKey => this.getError(errorKey) || []) : this.getError(key);
        return errors ? errors.map(error => {
            const translatedError = this.translate.instant('FORM.ERRORS.' + error.toUpperCase());
            return translatedError ? translatedError : '';
        }).filter(error => error !== '').join('\n') : '';
    }

    // WARNINGS //
    hasWarning(key: string): number {
        const warnings = this.warnings();
        if (!warnings) return 0;
        const warningItems = warnings.predicate();
        return warningItems.filter(w => w.key === key).length;
    }

    getWarningTooltip(key: string): string {
        const warnings = this.warnings();
        if (!warnings) return '';
        const warningItems = warnings.predicate();
        const keyWarnings = warningItems.filter(w => w.key === key);
        return keyWarnings
            .flatMap(w => w.messages || [])
            .map(msg => {
                const translated = this.translate.instant(msg);
                return translated || '';
            })
            .filter(msg => msg !== '')
            .join('\n');
    }


    hasRedDot(key: any): boolean {
        const header = this.headers().find(header => header.key === key);
        return header && header.hasRedDot ? header.hasRedDot() : false;
    }

    isFormEmpty(key?: string): boolean {
        const control: FormGroup | FormControl | FormArray = key ? (this.controls[key] as FormGroup | FormControl | FormArray) : this.form;

        let value: any;
        if (control instanceof FormGroup) {
            value = _.pickBy(control.getRawValue(), (v, k) => control.get(k)?.validator?.({} as AbstractControl)?.required);
        } else if (control instanceof FormArray) {
            value = control.getRawValue();
        } else {
            value = [control.value];
        }
        return _.every(value, v => v === null || v === undefined || v === '');
    }

    isSubFormValid(key?: string): boolean {
        const control = key ? this.controls[key] : this.form;
        if (_.isNil(control)) {
            return false;
        }
        const validable = this.validable();
        if (validable) {
            return control.valid && validable.isValid(key);
        }
        // console.log('control', key, control.valid);
        return control.valid;
    }

    scrollTo(fragment: string): void {
        try {
            const element = document.getElementById(fragment);
            if (element) {
                setTimeout(() => {
                    const yOffset = -10 * parseFloat(getComputedStyle(document.documentElement).fontSize);
                    const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
                    window.scrollTo({ top: y, behavior: 'smooth' });
                }, 0);
            }
        } catch (err) {
            console.error(err);
        }
    }



}